import { gql } from '@soundxyz/gql-string';
import { FEATURES, PLAYLIST_INFO_ACTIONS } from '@soundxyz/utils/constants/logging';
import {
  type FragmentType,
  getFragment,
  PlaylistCardInfoHookFragmentDoc,
} from '@graphql/reactQuery';
import { useLogInfo } from '@hooks/useLogInfo';
import { usePlaylistPlayingState } from '@hooks/usePlaylistPlayingState';
import { webappUriPath } from '@utils/url';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment PlaylistCardInfoHook on Shelf {
    id
    name
    releaseCount
    playTimeInSeconds
    type
    webappUri
    coverReleases {
      id
      coverImage {
        id
        url
      }
      staticCoverImage {
        id
        url
      }
    }
    user {
      id
      username
    }
    coverImage {
      id
      url
    }
  }
`);

export const usePlaylistCardInfo = ({
  data,
}: {
  data: FragmentType<PlaylistCardInfoHookFragmentDoc>;
}) => {
  const logInfo = useLogInfo();
  const playlist = getFragment(PlaylistCardInfoHookFragmentDoc, data);
  const { isPlaying, isActivePlaylist, loading, onPlayButtonClick } = usePlaylistPlayingState({
    playlistId: playlist.id,
  });

  const isUserCreated = playlist.type === 'DEFAULT';

  const onClick = async () => {
    logInfo({
      feature: FEATURES.PLAYLISTS,
      isArtistOnlyPage: false,
      message: 'Playlist card play button clicked',
      action: PLAYLIST_INFO_ACTIONS.PLAYLIST_CARD_PLAY_CLICK,
      data: {
        playlistId: playlist.id,
        isUserCreated,
      },
    });
    onPlayButtonClick();
  };

  return {
    id: playlist.id,
    /**
     * Name of playlist
     */
    playlistName: playlist.name,
    /**
     * Number of releases in playlist
     */
    releaseCount: playlist.releaseCount,
    /**
     * Total play time of playlist in seconds
     */
    playTimeInSeconds: playlist.playTimeInSeconds,
    /**
     * Is this playlist created by a human?
     */
    isUserCreated,
    /**
     * Is this playlist currently playing?
     */
    isPlaying,
    /**
     * Is this playlist currently active?
     */
    isActivePlaylist,
    /**
     * Is this playlist currently loading?
     */
    isBufferingPlaylist: loading,
    /**
     * Function to call when clicking on the play button
     */
    onClick: onClick,
    /**
     * Path to playlist page
     */
    playlistPagePath: webappUriPath(playlist.webappUri),
    /**
     * Type of playlist
     */
    type: playlist.type,
    /**
     * Playlist user username
     */
    username: playlist.user.username,
    /**
     * Playlist cover releases
     */
    coverReleases: playlist.coverReleases,

    /**
     * Playlist cover image uploaded by user
     */
    coverImage: playlist.coverImage,
  };
};
